import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronUp,
  Copy,
  Database,
  ExternalLink,
  Eye,
  FileText,
  Filter,
  Info,
  ListChecks,
  Loader2,
  Mail,
  MoreVertical,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  User,
  X,
} from 'lucide-react';

import Loader from '../components/Loader';
import SecureDocumentViewer from '../components/SecureDocumentViewer';
import { piiAPI } from '../services/api';
import PIIViewerModal from '../components/PIIViewerModal';
import {
  canOpenDocument,
  canOpenPII,
  documentAccessScope,
  piiAccessScope,
  lifecycleLabel,
  lifecycleTone,
  normalizeRevocation,
} from '../utils/lifecycleProjection.mjs';

const IST_TIME_ZONE = 'Asia/Kolkata';

const defaultFilters = {
  search: '',
  consent_status: '',
  lifecycle: 'all',
  privacy_notice_title: '',
  has_documents: '',
  document_type: '',
  has_retained_data: '',
  has_deleted_data: '',
  legal_hold: '',
  submitted_timeframe: '7d',
  created_after: '',
  created_before: '',
};

const TIMEFRAME_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

function valueOrDash(value, fallback = '—') {
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
}

async function copyToClipboard(value) {
  if (!value) return false;

  try {
    await navigator.clipboard.writeText(String(value));
    return true;
  } catch {
    return false;
  }
}

function DetailRow({ label, value, mono = false }) {
  return (
    <div className="grid gap-1 border-b border-slate-100 py-2 last:border-b-0 sm:grid-cols-[190px_1fr]">
      <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
        {label}
      </div>

      <div
        className={cx(
          'break-words text-sm text-slate-700',
          mono && 'font-mono text-xs',
        )}
      >
        {valueOrDash(value)}
      </div>
    </div>
  );
}

function PillList({ values, emptyText = '—' }) {
  const safeValues = Array.isArray(values)
    ? values.filter(Boolean)
    : values
      ? [values]
      : [];

  if (!safeValues.length) {
    return <span className="text-sm text-slate-500">{emptyText}</span>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {safeValues.map((value) => (
        <span
          key={String(value)}
          className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200"
        >
          {String(value)}
        </span>
      ))}
    </div>
  );
}

function formatDate(value) {
  if (!value) return '—';

  try {
    return new Date(value).toLocaleString('en-IN', {
      timeZone: IST_TIME_ZONE,
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(value);
  }
}

function formatDateOnly(value) {
  if (!value) return '—';

  try {
    return new Date(value).toLocaleDateString('en-IN', {
      timeZone: IST_TIME_ZONE,
      dateStyle: 'medium',
    });
  } catch {
    return String(value);
  }
}

function formatDateInputInIST(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: IST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const values = Object.fromEntries(
    parts.map(({ type, value }) => [type, value]),
  );

  return `${values.year}-${values.month}-${values.day}`;
}

function parseDateText(dateText) {
  const [year, month, day] = String(dateText || '')
    .split('-')
    .map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return {
    year,
    month,
    day,
  };
}

function shiftDateText(dateText, numberOfDays) {
  const parsed = parseDateText(dateText);

  if (!parsed) {
    return '';
  }

  const date = new Date(
    Date.UTC(
      parsed.year,
      parsed.month - 1,
      parsed.day + numberOfDays,
    ),
  );

  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

function startOfISTDayAsUTC(dateText) {
  const parsed = parseDateText(dateText);

  if (!parsed) {
    return '';
  }

  // Midnight IST is 18:30 UTC on the previous calendar day.
  const utcMilliseconds =
    Date.UTC(
      parsed.year,
      parsed.month - 1,
      parsed.day,
      0,
      0,
      0,
      0,
    ) -
    5.5 * 60 * 60 * 1000;

  return new Date(utcMilliseconds).toISOString();
}

function endOfISTDayAsUTC(dateText) {
  const nextDateText = shiftDateText(dateText, 1);
  const nextDayStart = startOfISTDayAsUTC(nextDateText);

  if (!nextDayStart) {
    return '';
  }

  return new Date(
    new Date(nextDayStart).getTime() - 1,
  ).toISOString();
}

function getTimeframeRange(value) {
  if (!value || value === 'all' || value === 'custom') {
    return {
      created_after: '',
      created_before: '',
    };
  }

  const todayInIST = formatDateInputInIST(new Date());
  let startDateText = todayInIST;

  if (value === '7d') {
    startDateText = shiftDateText(todayInIST, -6);
  } else if (value === '30d') {
    startDateText = shiftDateText(todayInIST, -29);
  } else if (value === '90d') {
    startDateText = shiftDateText(todayInIST, -89);
  }

  return {
    created_after: startOfISTDayAsUTC(startDateText),
    created_before: endOfISTDayAsUTC(todayInIST),
  };
}

  

function truncateMiddle(value, start = 10, end = 8) {
  if (!value) return '—';

  const text = String(value);
  if (text.length <= start + end + 3) return text;

  return `${text.slice(0, start)}...${text.slice(-end)}`;
}

function getRecordUuid(record) {
  return (
    record?.uuid ||
    record?.id ||
    record?.pii_record_uuid ||
    record?.record_uuid ||
    ''
  );
}

function getGroupsFromResponse(data) {
  if (Array.isArray(data?.groups)) return data.groups;
  if (Array.isArray(data?.results) && data?.view === 'grouped') return data.results;
  return [];
}

function getGroupRecords(group) {
  return Array.isArray(group?.records) ? group.records : [];
}

function getRecordDocuments(record) {
  return Array.isArray(record?.documents) ? record.documents : [];
}

function getRecordTitle(record) {
  return (
    record?.display_title ||
    record?.privacy_notice_title ||
    record?.consent_summary?.notice_title ||
    'PII Submission'
  );
}

function getPurposeLabel(record) {
  return (
    record?.purpose_label ||
    record?.consent_summary?.processing_purpose ||
    (Array.isArray(record?.consent_summary?.purpose_codes)
      ? record.consent_summary.purpose_codes.slice(0, 2).join(', ')
      : '') ||
    'Purpose not specified'
  );
}

function getConsentStatus(record) {
  return (
    record?.consent_status ||
    record?.consent_summary?.status ||
    ''
  );
}

function ConsentBadge({ status, available }) {
  const normalized = String(status || '').toUpperCase();

  if (!available) {
    return (
      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 ring-1 ring-slate-200">
        No consent
      </span>
    );
  }

  const healthy = ['ACTIVE', 'SIGNED', 'APPROVED', 'VALID'].includes(normalized);

  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ring-1',
        healthy
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
          : 'bg-amber-50 text-amber-700 ring-amber-100',
      )}
    >
      <ShieldCheck className="h-3 w-3" />
      {healthy
        ? 'Consent active'
        : normalized === 'REVOKED'
          ? 'Consent revoked'
          : `Consent ${normalized.replaceAll('_', ' ').toLowerCase() || 'linked'}`}
    </span>
  );
}

function LifecycleBadge({ record }) {
  const revocation = normalizeRevocation(record);
  const tone = lifecycleTone(revocation.state);
  const classes = {
    success: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    danger: 'bg-red-50 text-red-700 ring-red-100',
    warning: 'bg-amber-50 text-amber-700 ring-amber-100',
    neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
  }[tone] || 'bg-slate-100 text-slate-700 ring-slate-200';

  return (
    <span className={cx('inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ring-1', classes)}>
      <ShieldAlert className="h-3 w-3" />
      {lifecycleLabel(revocation.state)}
    </span>
  );
}

function MetricCard({ icon: Icon, title, value, subtitle, tone = 'orange' }) {
  const toneClass =
    tone === 'emerald'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
      : tone === 'amber'
        ? 'bg-amber-50 text-amber-700 ring-amber-100'
        : tone === 'slate'
          ? 'bg-slate-100 text-slate-700 ring-slate-200'
          : 'bg-orange-50 text-orange-700 ring-orange-100';

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-500">{title}</p>
          <p className="mt-2 text-3xl font-black text-slate-950">{value}</p>
          {subtitle && <p className="mt-1 text-xs text-slate-500">{subtitle}</p>}
        </div>

        <div className={cx('rounded-2xl p-3 ring-1', toneClass)}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function OverviewPanel({ summary, loading }) {
  return (
    <section className="rounded-[2rem] border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-white p-5 shadow-sm">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-bold text-orange-700 ring-1 ring-orange-100">
            <Sparkles className="h-3.5 w-3.5" />
            Data subject inventory
          </div>

          <h2 className="mt-3 text-xl font-black text-slate-950">
            Grouped PII records by person and consent context
          </h2>

          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            Find a person, review their PII submissions, then access PII data or documents through governed workflows.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-5 xl:min-w-[860px]">
          <MetricCard
            icon={User}
            title="Total subjects"
            value={loading ? '—' : summary.subjects}
            subtitle="Grouped people"
            tone="orange"
          />

          <MetricCard
            icon={Database}
            title="PII submissions"
            value={loading ? '—' : summary.records}
            subtitle="Submitted records"
            tone="slate"
          />

          <MetricCard
            icon={ShieldCheck}
            title="Consent linked"
            value={loading ? '—' : summary.consents}
            subtitle="Unique consents"
            tone="emerald"
          />

          <MetricCard
            icon={FileText}
            title="Documents"
            value={loading ? '—' : summary.documents}
            subtitle="Available evidence"
            tone="amber"
          />

          <MetricCard
            icon={Calendar}
            title="Latest submission"
            value={loading ? '—' : summary.recentlyUpdated}
            subtitle="Submission date"
            tone="slate"
          />
        </div>
      </div>
    </section>
  );
}

function FilterPanel({
  filters,
  activeFilterCount,
  onChange,
  onApply,
  onReset,
  onClose,
}) {
  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-black text-slate-950">Filter PII records</h2>
          <p className="mt-1 text-sm text-slate-500">
            Search by person, notice, consent, lifecycle, retention state, document type, or submission date.
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
        <div className="xl:col-span-2">
          <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Search
          </label>

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={onChange}
              placeholder="Name, email, employee ID, consent, document"
              className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
            />
          </div>
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Consent status
          </label>

          <select
            name="consent_status"
            value={filters.consent_status}
            onChange={onChange}
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
          >
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="SIGNED">Signed</option>
            <option value="APPROVED">Approved</option>
            <option value="REVOKED">Revoked</option>
            <option value="EXPIRED">Expired</option>
          </select>
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Lifecycle
          </label>

          <select
            name="lifecycle"
            value={filters.lifecycle}
            onChange={onChange}
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
          >
            <option value="all">All lifecycle records</option>
            <option value="active">Active</option>
            <option value="revocation_processing">Revocation processing</option>
            <option value="revoked_deleted">Revoked — deleted</option>
            <option value="revoked_retained">Revoked — retained and restricted</option>
            <option value="retry_required">Retry required</option>
            <option value="manual_review">Manual review</option>
          </select>
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Document availability
          </label>

          <select
            name="has_documents"
            value={filters.has_documents}
            onChange={onChange}
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
          >
            <option value="">Any</option>
            <option value="true">Has documents</option>
            <option value="false">No documents</option>
          </select>
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Document type
          </label>

          <input
            name="document_type"
            value={filters.document_type}
            onChange={onChange}
            placeholder="passport, pan, licence"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
          />
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Submission timeframe
          </label>

          <select
            name="submitted_timeframe"
            value={filters.submitted_timeframe}
            onChange={onChange}
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
          >
            {TIMEFRAME_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="xl:col-span-2">
          <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Privacy notice
          </label>

          <input
            name="privacy_notice_title"
            value={filters.privacy_notice_title}
            onChange={onChange}
            placeholder="New User Onboarding Notice"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
          />
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Retained data
          </label>
          <select name="has_retained_data" value={filters.has_retained_data} onChange={onChange} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100">
            <option value="">Any</option>
            <option value="true">Has retained data</option>
            <option value="false">No retained data</option>
          </select>
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Deleted data
          </label>
          <select name="has_deleted_data" value={filters.has_deleted_data} onChange={onChange} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100">
            <option value="">Any</option>
            <option value="true">Has deleted data</option>
            <option value="false">No deleted data</option>
          </select>
        </div>

        <div>
          <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Legal hold
          </label>
          <select name="legal_hold" value={filters.legal_hold} onChange={onChange} className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100">
            <option value="">Any</option>
            <option value="true">Legal hold active</option>
            <option value="false">No legal hold</option>
          </select>
        </div>

        {filters.submitted_timeframe === 'custom' && (
          <>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
                Submitted after
              </label>

              <input
                type="date"
                name="created_after"
                value={filters.created_after}
                onChange={onChange}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">
                Submitted before
              </label>

              <input
                type="date"
                name="created_before"
                value={filters.created_before}
                onChange={onChange}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              />
            </div>
          </>
        )}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onApply}
          className="rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700"
        >
          Apply filters
        </button>

        <button
          type="button"
          onClick={onReset}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
        >
          Reset
        </button>

        {activeFilterCount > 0 && (
          <p className="text-sm text-slate-500">
            {activeFilterCount} active filter{activeFilterCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </section>
  );
}

function SubjectIdentity({ subject }) {
  return (
    <div className="flex min-w-0 items-start gap-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-100 text-orange-700 ring-1 ring-orange-200">
        <User className="h-6 w-6" />
      </div>

      <div className="min-w-0">
        <h3 className="truncate text-base font-black text-slate-950">
          {valueOrDash(subject?.display_name, 'Unknown user')}
        </h3>

        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
          <span>
            Employee ID:{' '}
            <span className="font-semibold text-slate-800">
              {valueOrDash(subject?.employee_id, 'Unavailable')}
            </span>
          </span>

          <span className="inline-flex min-w-0 items-center gap-1.5">
            <Mail className="h-3.5 w-3.5 text-slate-400" />
            <span className="truncate">{valueOrDash(subject?.email, 'Email unavailable')}</span>
          </span>
        </div>

        <p className="mt-2 text-xs text-slate-400">
          Source: {subject?.source || 'governed identity context'}
        </p>
      </div>
    </div>
  );
}

function SubmissionActionsMenu({
  record,
  open,
  onToggle,
  onOpenDetail,
  onCopyRecordId,
  onCopyConsentId,
}) {
  const recordUuid = getRecordUuid(record);
  const consentId = record?.consent_id || record?.consent_summary?.consent_id || '';

  return (
    <div className="relative z-50">
      <button
        type="button"
        onClick={() => onToggle(recordUuid)}
        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
        title="More actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-[999] w-72 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
              Record actions
            </p>
            <p className="mt-1 truncate text-sm font-bold text-slate-900">
              {getRecordTitle(record)}
            </p>
          </div>

          <div className="py-2">
            <button
              type="button"
              onClick={() => onOpenDetail('governance', record)}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Info className="h-4 w-4 text-slate-400" />
              View governance metadata
            </button>

            <button
              type="button"
              onClick={() => onOpenDetail('consent', record)}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <ShieldCheck className="h-4 w-4 text-slate-400" />
              View consent evidence
            </button>

            <button
              type="button"
              onClick={() => onOpenDetail('fields', record)}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <ListChecks className="h-4 w-4 text-slate-400" />
              View field inventory
            </button>

          </div>

          <div className="border-t border-slate-100 py-2">
            <button
              type="button"
              onClick={() => onCopyRecordId(record)}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Copy className="h-4 w-4 text-slate-400" />
              Copy PII record ID
            </button>

            <button
              type="button"
              onClick={() => onCopyConsentId(record)}
              disabled={!consentId}
              className={cx(
                'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm font-semibold hover:bg-slate-50',
                consentId ? 'text-slate-700' : 'cursor-not-allowed text-slate-300',
              )}
            >
              <Copy className="h-4 w-4 text-slate-400" />
              Copy consent ID
            </button>
          </div>

          <div className="border-t border-slate-100 bg-slate-50 px-4 py-3">
            <p className="font-mono text-[11px] text-slate-400">
              {truncateMiddle(recordUuid, 16, 10)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function GovernanceMetadataPanel({ record }) {
  const subject = record?.subject || {};
  const consent = record?.consent_summary || {};
  const actions = record?.actions || {};

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-black text-slate-950">Record governance metadata</h3>
        <p className="mt-1 text-sm text-slate-500">
          Operational context used to identify the subject and govern access.
        </p>

        <div className="mt-4">
          <DetailRow label="PII Record ID" value={getRecordUuid(record)} mono />
          <DetailRow label="Display title" value={getRecordTitle(record)} />
          <DetailRow label="Purpose" value={getPurposeLabel(record)} />
          <DetailRow label="Record source" value={subject.source || record?.identity_preview?.source} />
          <DetailRow label="Subject name" value={subject.display_name || record?.identity_preview?.full_name} />
          <DetailRow label="Employee ID" value={subject.employee_id || record?.identity_preview?.employee_id} />
          <DetailRow label="Subject UUID" value={subject.user_uuid || record?.user_uuid} mono />
          <DetailRow label="Principal UUID" value={subject.principal_uuid} mono />
          <DetailRow label="Principal type" value={subject.principal_type} />
          <DetailRow label="Principal match confidence" value={subject.match_confidence} />
          <DetailRow label="Privacy notice" value={consent.notice_title || record?.privacy_notice_title} />
          <DetailRow label="Retention until" value={formatDate(record?.retention_until)} />
          <DetailRow label="Created" value={formatDate(record?.created_at)} />
          <DetailRow label="Updated" value={formatDate(record?.updated_at)} />
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-black text-slate-950">Available governed actions</h3>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-bold uppercase text-slate-400">PII View</p>
            <p className="mt-1 text-sm font-bold text-slate-800">
              {actions.can_view_pii ? 'Allowed' : 'Not allowed'}
            </p>
          </div>

          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-bold uppercase text-slate-400">Documents</p>
            <p className="mt-1 text-sm font-bold text-slate-800">
              {actions.can_view_documents ? 'Allowed' : 'Not allowed'}
            </p>
          </div>

          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-bold uppercase text-slate-400">Consent Evidence</p>
            <p className="mt-1 text-sm font-bold text-slate-800">
              {actions.can_view_consent ? 'Allowed' : 'Not allowed'}
            </p>
          </div>

          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-bold uppercase text-slate-400">Audit Trail</p>
            <p className="mt-1 text-sm font-bold text-slate-800">
              {actions.can_view_audit_trail ? 'Available' : 'Not available'}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function ConsentEvidencePanel({ record }) {
  const consent = record?.consent_summary || {};

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="font-black text-slate-950">Consent evidence</h3>
      <p className="mt-1 text-sm text-slate-500">
        Consent context linked to this PII submission.
      </p>

      <div className="mt-4">
        <DetailRow label="Consent available" value={consent.available ? 'Yes' : 'No'} />
        <DetailRow label="Consent ID" value={consent.consent_id || record?.consent_id} mono />
        <DetailRow label="Consent status" value={consent.status || getConsentStatus(record)} />
        <DetailRow label="Privacy notice" value={consent.notice_title || record?.privacy_notice_title} />
        <DetailRow label="Notice UUID" value={consent.notice_uuid || record?.privacy_notice_uuid} mono />
        <DetailRow label="Signed at" value={formatDate(consent.signed_at)} />
        <DetailRow label="Lawful basis" value={consent.lawful_basis} />
        <DetailRow label="Processing purpose" value={consent.processing_purpose} />
      </div>

      <div className="mt-5">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
          Purpose codes
        </p>
        <PillList values={consent.purpose_codes} emptyText="No purpose codes stored." />
      </div>

      {(consent.canonical_hash || consent.signature_hash || consent.receipt_jws_hash || consent.snapshot_hash) && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <h4 className="font-bold text-slate-900">Evidence hashes</h4>

          <div className="mt-3">
            <DetailRow label="Canonical hash" value={consent.canonical_hash} mono />
            <DetailRow label="Signature hash" value={consent.signature_hash} mono />
            <DetailRow label="Receipt JWS hash" value={consent.receipt_jws_hash} mono />
            <DetailRow label="Snapshot hash" value={consent.snapshot_hash} mono />
          </div>
        </div>
      )}
    </section>
  );
}

function FieldInventoryPanel({ record }) {
  const recordUuid = getRecordUuid(record);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inventory, setInventory] = useState(null);

  useEffect(() => {
    let active = true;

    async function loadInventory() {
      if (!recordUuid) {
        setError('PII record UUID is missing.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await piiAPI.getRecordFields(recordUuid);

        if (!active) return;

        setInventory(response.data || null);
      } catch (err) {
        if (!active) return;

        setError(
          err.response?.data?.detail ||
            err.response?.data?.error ||
            err.message ||
            'Failed to load field inventory.',
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadInventory();

    return () => {
      active = false;
    };
  }, [recordUuid]);

  const fields = useMemo(() => {
    if (Array.isArray(inventory?.fields)) return inventory.fields;
    if (Array.isArray(inventory?.results)) return inventory.results;
    if (Array.isArray(inventory?.field_inventory?.fields)) return inventory.field_inventory.fields;
    return [];
  }, [inventory]);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="font-black text-slate-950">Field inventory</h3>
      <p className="mt-1 text-sm text-slate-500">
        Field names and classifications only. Raw PII values are not returned here.
      </p>

      {loading && (
        <div className="mt-6 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
          Loading field inventory
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && fields.length === 0 && (
        <div className="mt-5 rounded-xl border border-dashed border-slate-300 p-8 text-center">
          <ListChecks className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 text-sm font-semibold text-slate-700">No field inventory available</p>
          <p className="mt-1 text-xs text-slate-500">
            The record may have been created before field inventory sync was enabled.
          </p>
        </div>
      )}

      {!loading && !error && fields.length > 0 && (
        <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200">
          <div className="grid grid-cols-[1.2fr_1fr_1fr] gap-3 bg-slate-50 px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-500">
            <div>Field</div>
            <div>Type</div>
            <div>Sensitivity</div>
          </div>

          <div className="divide-y divide-slate-100">
            {fields.map((field) => (
              <div
                key={field.id || field.normalized_field_name || field.field_name}
                className="grid grid-cols-[1.2fr_1fr_1fr] gap-3 px-4 py-3 text-sm"
              >
                <div>
                  <p className="font-semibold text-slate-900">
                    {field.field_name || field.normalized_field_name}
                  </p>
                  <p className="mt-0.5 font-mono text-xs text-slate-400">
                    {field.normalized_field_name}
                  </p>
                </div>

                <div className="text-slate-700">
                  {valueOrDash(field.field_type)}
                </div>

                <div>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200">
                    {valueOrDash(field.sensitivity)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function PIIRecordDetailDrawer({ detail, onClose }) {
  if (!detail?.record) return null;

  const { type, record } = detail;
  const title =
    type === 'governance'
      ? 'Governance metadata'
      : type === 'consent'
        ? 'Consent evidence'
        : 'Field inventory';

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/40 backdrop-blur-sm">
      <div className="h-full w-full max-w-4xl overflow-y-auto bg-slate-50 shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-orange-600">
                PII record details
              </p>

              <h2 className="mt-1 text-xl font-black text-slate-950">
                {title}
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                {getRecordTitle(record)}
              </p>
            </div>

            <div className="flex items-center gap-2">

              <button
                type="button"
                onClick={onClose}
                className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-5 p-6">
          {type === 'governance' && <GovernanceMetadataPanel record={record} />}
          {type === 'consent' && <ConsentEvidencePanel record={record} />}
          {type === 'fields' && <FieldInventoryPanel record={record} />}
        </div>
      </div>
    </div>
  );
}

function PIISubmissionCard({
  record,
  expanded,
  openMenu,
  onToggle,
  onToggleMenu,
  onViewPII,
  onViewDocument,
  onOpenDetail,
  onOpenAuditTrail,
  onCopyRecordId,
  onCopyConsentId,
}) {
  const recordUuid = getRecordUuid(record);
  const documents = getRecordDocuments(record);
  const consentStatus = getConsentStatus(record);
  const revocation = normalizeRevocation(record);
  const piiAccessible = canOpenPII(record);

  return (
    <article className="relative overflow-visible rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="grid gap-4 p-4 xl:grid-cols-[1.4fr_1fr_1fr_auto] xl:items-center">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-bold text-orange-700 ring-1 ring-orange-100">
              PII submission
            </span>

            <ConsentBadge
              status={consentStatus}
              available={record.consent_context_available}
            />

            <LifecycleBadge record={record} />
          </div>

          <h4 className="mt-2 truncate text-sm font-black text-slate-950" title={getRecordTitle(record)}>
            {getRecordTitle(record)}
          </h4>

          <p className="mt-1 text-xs text-slate-500">
            Purpose: <span className="font-semibold text-slate-700">{getPurposeLabel(record)}</span>
          </p>

          {revocation.available && (
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-emerald-50 px-2 py-1 font-bold text-emerald-700 ring-1 ring-emerald-100">
                {revocation.counts.deleted} deleted
              </span>
              <span className="rounded-full bg-amber-50 px-2 py-1 font-bold text-amber-700 ring-1 ring-amber-100">
                {revocation.counts.retained} retained
              </span>
              {revocation.has_legal_hold && (
                <span className="rounded-full bg-red-50 px-2 py-1 font-bold text-red-700 ring-1 ring-red-100">
                  Legal hold
                </span>
              )}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
            Evidence
          </p>

          <div className="mt-2 flex flex-wrap gap-2">
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200">
              {documents.length} document{documents.length !== 1 ? 's' : ''}
            </span>

            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200">
              {record.field_count || 0} fields
            </span>
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
            Submitted
          </p>
          <p className="mt-2 text-sm font-semibold text-slate-800">
            {formatDate(record.created_at || record.submitted_at || record.updated_at)}
          </p>
          <p className="mt-1 font-mono text-xs text-slate-400">
            {truncateMiddle(recordUuid, 12, 8)}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2">
          <SubmissionActionsMenu
            record={record}
            open={openMenu}
            onToggle={onToggleMenu}
            onOpenDetail={onOpenDetail}
            onCopyRecordId={onCopyRecordId}
            onCopyConsentId={onCopyConsentId}
          />

          <button
            type="button"
            onClick={() => onToggle(recordUuid)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            {expanded ? (
              <>
                Hide
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                Open
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="grid items-stretch gap-4 border-t border-slate-100 bg-slate-50/70 p-4 lg:grid-cols-2">
          <section className="flex min-h-[260px] flex-col rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h5 className="font-black text-slate-950">PII Data</h5>
                <p className="mt-1 text-sm text-slate-500">
                  {piiAccessScope(record) === 'retained'
                    ? 'Only lawfully retained fields are available through an exceptional governed access session.'
                    : piiAccessible
                      ? 'Access submitted PII through the governed secure viewer.'
                      : 'No PII fields remain available for viewing.'}
                </p>
              </div>

              <div className="rounded-xl bg-orange-50 p-2 text-orange-700 ring-1 ring-orange-100">
                <ShieldAlert className="h-4 w-4" />
              </div>
            </div>

            <div className="mt-5 flex-1 space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Privacy Notice</span>
                <span className="max-w-[320px] truncate text-right font-semibold text-slate-800">
                  {valueOrDash(record.privacy_notice_title || record.consent_summary?.notice_title, 'Notice unavailable')}
                </span>
              </div>

              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Purpose</span>
                <span className="max-w-[320px] truncate text-right font-semibold text-slate-800">
                  {getPurposeLabel(record)}
                </span>
              </div>

              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Consent ID</span>
                <span className="font-mono text-xs text-slate-800">
                  {truncateMiddle(record.consent_id, 16, 10)}
                </span>
              </div>

              <div className="flex justify-between gap-4">
                <span className="text-slate-500">Record ID</span>
                <span className="font-mono text-xs text-slate-800">
                  {truncateMiddle(recordUuid, 16, 10)}
                </span>
              </div>
            </div>

            <div className="mt-5 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => onViewPII(record)}
                disabled={!piiAccessible}
                title={!piiAccessible ? 'No viewable PII remains for this record.' : undefined}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                <ShieldAlert className="h-4 w-4" />
                {piiAccessScope(record) === 'retained' ? 'View retained PII' : piiAccessible ? 'Secure PII View' : 'PII unavailable'}
              </button>
            </div>
          </section>

          <section className="flex min-h-[260px] flex-col rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h5 className="font-black text-slate-950">Documents</h5>
                <p className="mt-1 text-sm text-slate-500">
                  View linked evidence documents through secure document sessions.
                </p>
              </div>

              <div className="rounded-xl bg-orange-50 p-2 text-orange-700 ring-1 ring-orange-100">
                <FileText className="h-4 w-4" />
              </div>
            </div>

            <div className="mt-5 flex-1 space-y-3">
              {documents.length ? (
                documents.map((document) => {
                  const documentAccessible = canOpenDocument(record, document);
                  return (
                  <div
                    key={document.id || document.document_id}
                    className="rounded-xl border border-slate-200 bg-white p-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-slate-950">
                        {document.document_type || 'Document'}
                      </p>
                      <p className="mt-1 truncate text-xs text-slate-500">
                        {document.original_filename || 'Filename unavailable'}
                      </p>
                      <p className={`mt-2 text-xs font-semibold ${
                        document.is_deleted || String(document.lifecycle_state || '').toUpperCase() === 'DELETED'
                          ? 'text-emerald-700'
                          : documentAccessScope(record, document) === 'retained'
                            ? 'text-amber-700'
                            : documentAccessible
                              ? 'text-emerald-700'
                              : 'text-slate-500'
                      }`}>
                        {document.is_deleted || String(document.lifecycle_state || '').toUpperCase() === 'DELETED'
                          ? 'Deleted after consent withdrawal'
                          : documentAccessScope(record, document) === 'retained'
                            ? 'Retained · governed access available'
                            : documentAccessible
                              ? 'Active'
                              : 'Unavailable'}
                      </p>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => onViewDocument(record, document, 'modal', documentAccessible)}
                        disabled={!documentAccessible}
                        className="inline-flex items-center justify-center gap-1 rounded-xl bg-orange-600 px-3 py-2 text-xs font-bold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View
                      </button>

                      <button
                        type="button"
                        onClick={() => onViewDocument(record, document, 'fullpage', documentAccessible)}
                        disabled={!documentAccessible}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
                      >
                        Fullscreen
                      </button>
                    </div>
                  </div>
                  );
                })
              ) : (
                <div className="flex h-full min-h-[130px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 p-6 text-center">
                  <FileText className="h-8 w-8 text-slate-300" />
                  <p className="mt-2 text-sm font-semibold text-slate-700">No documents linked</p>
                  <p className="mt-1 text-xs text-slate-500">
                    This PII submission does not currently have document evidence.
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </article>
  );
}

function SubjectGroupCard({
  group,
  expanded,
  expandedSubmissions,
  openMenuRecord,
  onToggleGroup,
  onToggleSubmission,
  onToggleMenu,
  onViewPII,
  onViewDocument,
  onOpenDetail,
  onOpenAuditTrail,
  onCopyRecordId,
  onCopyConsentId,
}) {
  const subject = group.subject || {};
  const summary = group.summary || {};
  const records = getGroupRecords(group);

  return (
    <article className="relative overflow-visible rounded-[1.75rem] border border-slate-200 bg-white shadow-sm transition hover:border-orange-200 hover:shadow-md">
      <div className="grid gap-5 p-5 xl:grid-cols-[1.35fr_1fr_1fr_auto] xl:items-center">
        <SubjectIdentity subject={subject} />

        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
            Submissions
          </p>

          <div className="mt-2 flex flex-wrap gap-2">
            <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-bold text-orange-700 ring-1 ring-orange-100">
              {summary.pii_record_count || records.length} PII submission{(summary.pii_record_count || records.length) !== 1 ? 's' : ''}
            </span>

            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-100">
              {summary.consent_count || 0} consent{summary.consent_count === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
            Evidence
          </p>

          <div className="mt-2 flex flex-wrap gap-2">
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 ring-1 ring-slate-200">
              {summary.document_count || 0} document{summary.document_count === 1 ? '' : 's'}
            </span>
          </div>

          <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
            <Calendar className="h-3.5 w-3.5" />
            Updated {formatDate(summary.latest_updated_at)}
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => onToggleGroup(group.group_key || subject.group_key)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            {expanded ? (
              <>
                Hide submissions
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                View submissions
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-3 border-t border-slate-100 bg-slate-50/70 p-5">
          {records.map((record, index) => {
            const recordUuid = getRecordUuid(record) || `${group.group_key}-${index}`;

            return (
              <PIISubmissionCard
                key={recordUuid}
                record={record}
                expanded={!!expandedSubmissions[recordUuid]}
                openMenu={openMenuRecord === recordUuid}
                onToggle={onToggleSubmission}
                onToggleMenu={onToggleMenu}
                onViewPII={onViewPII}
                onViewDocument={onViewDocument}
                onOpenDetail={onOpenDetail}
                onOpenAuditTrail={onOpenAuditTrail}
                onCopyRecordId={onCopyRecordId}
                onCopyConsentId={onCopyConsentId}
              />
            );
          })}
        </div>
      )}
    </article>
  );
}

export default function PIIRecordsPage() {
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [serverSummary, setServerSummary] = useState(null);

  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');

  const [showFilters, setShowFilters] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedSubmissions, setExpandedSubmissions] = useState({});
  const [openMenuRecord, setOpenMenuRecord] = useState('');
  const [detailDrawer, setDetailDrawer] = useState(null);

  const [viewingPII, setViewingPII] = useState(null);
  const [viewingDocument, setViewingDocument] = useState(null);

  const [filters, setFilters] = useState(defaultFilters);

  const activeFilterCount = useMemo(() => {
    return Object.entries(filters).filter(([key, value]) => {
      if (!value) return false;
      if (key === 'submitted_timeframe' && value === '7d') return false;
      if (key === 'lifecycle' && value === 'all') return false;
      return true;
    }).length;
  }, [filters]);

  const summary = useMemo(() => {
    const records = groups.flatMap((group) => getGroupRecords(group));

    const latestUpdated = groups
      .map((group) => group?.summary?.latest_submission_at || group?.summary?.latest_updated_at)
      .filter(Boolean)
      .sort()
      .reverse()[0];

    return {
      subjects: serverSummary?.subjects ?? groups.length,
      records: serverSummary?.pii_records ?? records.length,
      documents:
        serverSummary?.documents ??
        groups.reduce((total, group) => total + Number(group?.summary?.document_count || 0), 0),
      consents:
        serverSummary?.consents ??
        groups.reduce((total, group) => total + Number(group?.summary?.consent_count || 0), 0),
      recentlyUpdated: latestUpdated ? formatDateOnly(latestUpdated) : '—',
    };
  }, [groups, serverSummary]);

  const buildActiveFilters = (sourceFilters = filters) => {
    const params = {
      view: 'grouped',
      limit: 500,
    };

    Object.entries(sourceFilters).forEach(([key, value]) => {
      if (!value) return;

      if (key === 'submitted_timeframe') return;
      if (key === 'created_after' || key === 'created_before') return;
      if (key === 'lifecycle' && value === 'all') return;

      params[key] = value;
    });

    if (sourceFilters.submitted_timeframe === 'custom') {
      if (sourceFilters.created_after) {
        params.created_after = startOfISTDayAsUTC(
          sourceFilters.created_after,
        );
      }

      if (sourceFilters.created_before) {
        params.created_before = endOfISTDayAsUTC(
          sourceFilters.created_before,
        );
      }
    } else {
      const range = getTimeframeRange(sourceFilters.submitted_timeframe);

      if (range.created_after) {
        params.created_after = range.created_after;
      }

      if (range.created_before) {
        params.created_before = range.created_before;
      }
    }

    return params;
  };

  const loadRecords = async (appliedFilters = buildActiveFilters()) => {
    const isInitial = groups.length === 0;

    if (isInitial) setLoading(true);
    else setTableLoading(true);

    setError('');

    try {
      const response = await piiAPI.getRecords(appliedFilters);
      const data = response.data || {};

      setGroups(getGroupsFromResponse(data));
      setServerSummary(data.summary || null);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.error ||
          err.message ||
          'Failed to load PII records.',
      );
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    loadRecords(buildActiveFilters(defaultFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setFilters((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === 'submitted_timeframe' && value !== 'custom') {
        next.created_after = '';
        next.created_before = '';
      }

      return next;
    });
  };

  const applyFilters = () => {
    setExpandedGroups({});
    setExpandedSubmissions({});
    setOpenMenuRecord('');
    setDetailDrawer(null);
    loadRecords(buildActiveFilters(filters));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setExpandedGroups({});
    setExpandedSubmissions({});
    setOpenMenuRecord('');
    setDetailDrawer(null);
    loadRecords(buildActiveFilters(defaultFilters));
  };

  const refreshRecords = () => {
    setOpenMenuRecord('');
    loadRecords(buildActiveFilters(filters));
  };

  const runQuickSearch = () => {
  setExpandedGroups({});
  setExpandedSubmissions({});
  setOpenMenuRecord('');
  setDetailDrawer(null);

  loadRecords(buildActiveFilters(filters));
};

const clearQuickSearch = () => {
  const nextFilters = {
    ...filters,
    search: '',
  };

  setFilters(nextFilters);
  setExpandedGroups({});
  setExpandedSubmissions({});
  setOpenMenuRecord('');
  setDetailDrawer(null);

  loadRecords(buildActiveFilters(nextFilters));
};

const applyQuickTimeframe = (timeframe) => {
  const nextFilters = {
    ...filters,
    submitted_timeframe: timeframe,
    created_after: '',
    created_before: '',
  };

  setFilters(nextFilters);
  setExpandedGroups({});
  setExpandedSubmissions({});
  setOpenMenuRecord('');
  setDetailDrawer(null);

  loadRecords(buildActiveFilters(nextFilters));
};

  const toggleGroup = (groupKey) => {
    if (!groupKey) return;

    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const toggleSubmission = (recordUuid) => {
    if (!recordUuid) return;

    setExpandedSubmissions((prev) => ({
      ...prev,
      [recordUuid]: !prev[recordUuid],
    }));
  };


  const toggleSubmissionMenu = (recordUuid) => {
  if (!recordUuid) return;

  setOpenMenuRecord((prev) => (prev === recordUuid ? '' : recordUuid));
};

const openDetailDrawer = (type, record) => {
  setOpenMenuRecord('');
  setDetailDrawer({
    type,
    record,
  });
};



const handleCopyRecordId = async (record) => {
  const recordUuid = getRecordUuid(record);

  const copied = await copyToClipboard(recordUuid);

  setOpenMenuRecord('');

  if (!copied) {
    setError('Could not copy PII record ID.');
  }
};

const handleCopyConsentId = async (record) => {
  const consentId = record?.consent_id || record?.consent_summary?.consent_id || '';

  const copied = await copyToClipboard(consentId);

  setOpenMenuRecord('');

  if (!copied) {
    setError('Could not copy consent ID.');
  }
};

  const handleViewDocument = (record, document, mode = 'modal', accessible = true) => {
    const documentId = document?.id || document?.document_id || '';
    const accessScope = documentAccessScope(record, document);
    if (!accessible || !accessScope) {
      setError('This document was deleted or is not available for governed access.');
      return;
    }
    if (!documentId) {
      setError('Document ID is missing.');
      return;
    }
    if (mode === 'fullpage') {
      navigate(`/view-document/${documentId}?access_scope=${accessScope}`);
      return;
    }
    setViewingDocument({ documentId, accessScope });
  };

  const handleOpenPII = (record) => {
    if (!canOpenPII(record)) {
      setError('This record is revoked or processing-restricted. Raw PII cannot be opened.');
      return;
    }

    const recordUuid = getRecordUuid(record);

    if (!recordUuid) {
      setError('Cannot open Secure PII View because this record is missing its UUID.');
      return;
    }

    setViewingPII({
      recordUuid,
      consentId: record.consent_id || '',
      accessScope: piiAccessScope(record),
    });
  };

  if (loading) {
    return <Loader text="Loading PII records..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-2xl bg-orange-100 p-3 text-orange-700 ring-1 ring-orange-200">
            <Database className="h-6 w-6" />
          </div>

          <div>
            <h1 className="text-2xl font-black text-slate-950">PII Records</h1>
            <p className="mt-1 text-sm text-slate-500">
              Data subject grouped inventory with governed PII and document access.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setShowFilters((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            <Filter className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-orange-600 px-2 py-0.5 text-xs text-white">
                {activeFilterCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={refreshRecords}
            className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      <OverviewPanel summary={summary} loading={tableLoading} />

      {showFilters && (
        <FilterPanel
          filters={filters}
          activeFilterCount={activeFilterCount}
          onChange={handleFilterChange}
          onApply={applyFilters}
          onReset={resetFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5" />
            <div>
              <p className="font-bold">Something went wrong</p>
              <p className="mt-1 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
  <div className="shrink-0">
    <h2 className="font-black text-slate-950">
      Data subjects
    </h2>

    <p className="mt-1 text-sm text-slate-500">
      {groups.length} subject
      {groups.length !== 1 ? 's' : ''} found.
    </p>
  </div>

  <div className="flex w-full flex-col gap-3 xl:max-w-5xl">
    <form
      role="search"
      aria-label="Search PII records"
      onSubmit={(event) => {
        event.preventDefault();
        runQuickSearch();
      }}
      className="flex w-full flex-col gap-2 sm:flex-row"
    >
      <div className="relative min-w-0 flex-1">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
        />

        <input
          type="search"
          value={filters.search}
          onChange={(event) => {
            setFilters((previous) => ({
              ...previous,
              search: event.target.value,
            }));
          }}
          placeholder="Search name, email, employee ID, consent, record or document"
          aria-label="Search data subjects and PII records"
          className="w-full rounded-xl border border-slate-300 py-2.5 pl-10 pr-10 text-sm outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
        />

        {filters.search && (
          <button
            type="button"
            onClick={clearQuickSearch}
            aria-label="Clear record search"
            title="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <button
        type="submit"
        disabled={tableLoading}
        className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {tableLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Search className="h-4 w-4" />
        )}

        Search
      </button>

      <button
        type="button"
        onClick={() => setShowFilters(true)}
        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700"
      >
        <Filter className="h-4 w-4" />

        More filters

        {activeFilterCount > 0 && (
          <span className="rounded-full bg-orange-600 px-2 py-0.5 text-xs text-white">
            {activeFilterCount}
          </span>
        )}
      </button>
    </form>

    <div className="flex flex-wrap items-center gap-2">
      <span className="mr-1 text-xs font-bold uppercase tracking-wide text-slate-400">
        Submitted
      </span>

      {[
        {
          value: 'today',
          label: 'Today',
        },
        {
          value: '7d',
          label: 'Last 7 days',
        },
        {
          value: '30d',
          label: 'Last 30 days',
        },
        {
          value: 'all',
          label: 'All time',
        },
      ].map((option) => {
        const selected =
          filters.submitted_timeframe === option.value;

        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            onClick={() => {
              applyQuickTimeframe(option.value);
            }}
            className={cx(
              'rounded-full px-3 py-1.5 text-xs font-bold ring-1 transition',
              selected
                ? 'bg-orange-600 text-white ring-orange-600'
                : 'bg-white text-slate-600 ring-slate-200 hover:bg-orange-50 hover:text-orange-700 hover:ring-orange-200',
            )}
          >
            {option.label}
          </button>
        );
      })}

      {activeFilterCount > 0 && (
        <button
          type="button"
          onClick={resetFilters}
          className="ml-1 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-red-700 ring-1 ring-red-200 hover:bg-red-50"
        >
          <X className="h-3.5 w-3.5" />
          Clear filters
        </button>
      )}

      {tableLoading && (
        <span
          role="status"
          className="ml-auto inline-flex items-center gap-2 text-sm text-slate-500"
        >
          <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
          Updating records
        </span>
      )}
    </div>
  </div>
</div>

        <div className="space-y-4">
          {groups.map((group, index) => {
            const groupKey = group.group_key || group.subject?.group_key || `group-${index}`;

            return (
              <SubjectGroupCard
                key={groupKey}
                group={group}
                expanded={!!expandedGroups[groupKey]}
                expandedSubmissions={expandedSubmissions}
                openMenuRecord={openMenuRecord}
                onToggleGroup={toggleGroup}
                onToggleSubmission={toggleSubmission}
                onToggleMenu={toggleSubmissionMenu}
                onViewPII={handleOpenPII}
                onViewDocument={handleViewDocument}
                onOpenDetail={openDetailDrawer}
                onCopyRecordId={handleCopyRecordId}
                onCopyConsentId={handleCopyConsentId}
              />
            );
          })}

          {!tableLoading && groups.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center">
              <Search className="mx-auto h-12 w-12 text-slate-300" />
              <p className="mt-3 text-lg font-bold text-slate-700">No PII records found</p>
              <p className="mt-1 text-sm text-slate-500">
                {filters.search
  ? `No records matched “${filters.search}”. Try another name, email, employee ID, consent ID or record ID.`
  : activeFilterCount > 0
    ? 'No records match the selected timeframe or filters. Try Last 7 days or All time.'
    : 'No PII records have been submitted yet.'}
              </p>
            </div>
          )}
        </div>
      </section>

      {viewingDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="m-4 flex h-full max-h-screen w-full max-w-7xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4">
              <h3 className="text-lg font-black text-slate-950">Secure Document Viewer</h3>

              <button
                type="button"
                onClick={() => setViewingDocument(null)}
                className="rounded-xl p-2 text-slate-500 hover:bg-slate-200"
                title="Close viewer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden">
              <SecureDocumentViewer
                documentId={viewingDocument.documentId}
                accessScope={viewingDocument.accessScope}
                onClose={() => setViewingDocument(null)}
              />
            </div>
          </div>
        </div>
      )}

      {viewingPII && (
        <PIIViewerModal
          recordUuid={viewingPII.recordUuid}
          consentId={viewingPII.consentId}
          accessScope={viewingPII.accessScope}
          onClose={() => setViewingPII(null)}
        />
      )}

      {detailDrawer && (
        <PIIRecordDetailDrawer
          detail={detailDrawer}
          onClose={() => setDetailDrawer(null)}
        />
      )}
    </div>
  );
}